// utils/distance.js
/**
 * Calculates the distance between two coordinates using the Haversine formula
 *
 * @param {number} lat1 - Latitude of first coordinate
 * @param {number} lon1 - Longitude of first coordinate
 * @param {number} lat2 - Latitude of second coordinate
 * @param {number} lon2 - Longitude of second coordinate
 * @returns {number|null} - Distance in meters or null if coordinates are invalid
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
    // If any coordinates are null or undefined, return null
    if (!lat1 || !lon1 || !lat2 || !lon2) {
      return null;
    }

    // Earth's radius in meters
    const R = 6371e3;

    // Convert latitude and longitude from degrees to radians
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    // Haversine formula
    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    // Distance in meters
    const distance = R * c;

    return Math.round(distance);
  };

  // utils/statusHelper.js
  /**
   * Determines the status color based on the latest sync time
   *
   * @param {string} latestSync - ISO timestamp of the latest sync
   * @returns {string} - Color code ('green', 'yellow', or 'red')
   */
  export const getStatusColor = (latestSync) => {
    if (!latestSync) return 'red';

    const now = new Date();
    const syncTime = new Date(latestSync);
    const diffMinutes = (now - syncTime) / (1000 * 60);

    if (diffMinutes <= 15) return 'green';
    if (diffMinutes <= 30) return 'yellow';
    return 'red';
  };

  // utils/dateFormatter.js
  /**
   * Formats a date string into a readable format
   *
   * @param {string} dateString - ISO timestamp to format
   * @returns {string} - Formatted date string or 'N/A' if no date
   */
  export const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  /**
   * Formats coordinates to a readable string
   *
   * @param {number} latitude - Latitude coordinate
   * @param {number} longitude - Longitude coordinate
   * @param {number} decimals - Number of decimal places to display
   * @returns {string} - Formatted coordinates or 'Unknown' if coordinates are invalid
   */
  export const formatCoordinates = (latitude, longitude, decimals = 6) => {
    if (latitude === null || longitude === null ||
        latitude === undefined || longitude === undefined) {
      return 'Unknown';
    }

    return `${latitude.toFixed(decimals)}, ${longitude.toFixed(decimals)}`;
  };